import {
  BellOutlined,
  CheckCircleFilled,
  CheckOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { history } from '@umijs/max';
import { Badge, Button, Popover, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useCallback, useEffect, useState } from 'react';
import { SiteMessageDetailDrawer } from '@/components/SiteMessage';
import { SITE_MESSAGE_INBOX_PATH } from '@/constants/siteMessage';
import useSiteMessageUnreadCount from '@/hooks/useSiteMessageUnreadCount';
import {
  getSiteMessageList,
  markAllSiteMessageRead,
  type SiteMessageItem,
} from '@/services/notification/siteMessage';
import { subscribeSiteMessageUnreadDataChanged } from '@/utils/siteMessageUnread';

const useStyles = createStyles(({ token }) => ({
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
    fontSize: 18,
    cursor: 'pointer',
    color: '#fff',
    border: 'none',
    background: 'transparent',
    lineHeight: 1,
  },
  panel: {
    width: 360,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '4px 4px 12px',
    borderBottom: `1px solid ${token.colorSplit}`,
  },
  headerTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: token.colorTextHeading,
    lineHeight: 1.4,
  },
  unreadCount: {
    marginTop: 4,
    fontSize: 12,
    color: token.colorTextSecondary,
    lineHeight: 1.4,
  },
  list: {
    maxHeight: 320,
    overflowY: 'auto',
    margin: '0 -4px',
  },
  item: {
    display: 'flex',
    gap: 8,
    padding: '12px 8px',
    cursor: 'pointer',
    borderBottom: `1px solid ${token.colorSplit}`,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: token.colorFillTertiary,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  itemDot: {
    marginTop: 4,
    flexShrink: 0,
  },
  itemBody: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: token.colorTextHeading,
    lineHeight: 1.5,
    wordBreak: 'break-word',
  },
  itemTime: {
    marginTop: 4,
    fontSize: 12,
    color: token.colorTextSecondary,
    lineHeight: 1.4,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 48,
    color: token.colorSuccess,
    lineHeight: 1,
  },
  emptyTitle: {
    marginTop: 16,
    fontSize: 14,
    fontWeight: 500,
    color: token.colorText,
    lineHeight: 1.5,
  },
  emptyDesc: {
    marginTop: 4,
    fontSize: 12,
    color: token.colorTextSecondary,
    lineHeight: 1.5,
  },
  footer: {
    padding: '12px 4px 4px',
    borderTop: `1px solid ${token.colorSplit}`,
    textAlign: 'center',
  },
  footerLink: {
    padding: 0,
    height: 'auto',
    fontSize: 14,
    '&.ant-btn-link': {
      color: token.colorPrimary,
    },
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px 0',
  },
}));

const NotificationBell: React.FC = () => {
  const { styles } = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [notifications, setNotifications] = useState<SiteMessageItem[]>([]);
  const { unreadCount, refreshUnreadCount } = useSiteMessageUnreadCount({
    enablePolling: true,
    refreshOnMount: true,
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState<string>();

  const loadUnreadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSiteMessageList({ unread: true });
      setNotifications(res?.list || []);
    } catch (error) {
      console.error('获取站内信列表失败:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    return subscribeSiteMessageUnreadDataChanged(() => {
      if (open) {
        void loadUnreadNotifications();
      }
    });
  }, [loadUnreadNotifications, open]);

  useEffect(() => {
    if (open) {
      void loadUnreadNotifications();
    }
  }, [open, loadUnreadNotifications]);

  const handleMarkAllRead = async () => {
    if (markingAll || unreadCount === 0) {
      return;
    }
    setMarkingAll(true);
    try {
      await markAllSiteMessageRead();
      await refreshUnreadCount();
      setNotifications([]);
    } catch (error) {
      console.error('全部标记已读失败:', error);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleItemClick = (item: SiteMessageItem) => {
    setActiveMessageId(item.siteMessageId);
    setDetailOpen(true);
    setOpen(false);
  };

  const handleViewAll = () => {
    setOpen(false);
    history.push(SITE_MESSAGE_INBOX_PATH);
  };

  const content = (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.headerTitle}>消息通知</h3>
          <div className={styles.unreadCount}>
            {unreadCount > 0 ? `${unreadCount} 条未读` : '暂无未读'}
          </div>
        </div>
        {unreadCount > 0 ? (
          <Button
            icon={<CheckOutlined />}
            type="primary"
            ghost
            loading={markingAll}
            onClick={() => void handleMarkAllRead()}
          >
            全部已读
          </Button>
        ) : null}
      </div>

      <div className={styles.list}>
        {loading ? (
          <div className={styles.loadingWrap}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <div className={styles.empty}>
            <CheckCircleFilled className={styles.emptyIcon} />
            <div className={styles.emptyTitle}>暂无未读通知</div>
            <div className={styles.emptyDesc}>新通知将在这里显示</div>
          </div>
        ) : (
          notifications.map((item) => (
            <div
              key={item.siteMessageId}
              className={styles.item}
              onClick={() => handleItemClick(item)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleItemClick(item);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <span className={styles.itemDot}>
                <Badge status="error" />
              </span>
              <div className={styles.itemBody}>
                <div className={styles.itemTitle}>{item.title}</div>
                <div className={styles.itemTime}>{item.publishedAt || '-'}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className={styles.footer}>
        <Button
          type="link"
          className={styles.footerLink}
          onClick={handleViewAll}
        >
          查看全部通知 <RightOutlined />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        trigger="click"
        placement="bottomRight"
        arrow={false}
        open={open}
        onOpenChange={setOpen}
        content={content}
      >
        <button type="button" aria-label="消息通知" className={styles.trigger}>
          <Badge count={unreadCount} size="small" offset={[0, 0]}>
            <BellOutlined style={{ color: '#fff', fontSize: 20 }} />
          </Badge>
        </button>
      </Popover>

      <SiteMessageDetailDrawer
        open={detailOpen}
        siteMessageId={activeMessageId}
        onClose={() => {
          setDetailOpen(false);
          setActiveMessageId(undefined);
        }}
      />
    </>
  );
};

export default NotificationBell;
